import { BN } from '@coral-xyz/anchor'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import Decimal from 'decimal.js'
import { useEffect, useRef, useState } from 'react'
import { Card, Col, Container, Row } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import MainLayout from '../components/MainLayout'
import OverviewBar from '../components/OverviewBar'
import { SignSendConfirmTransaction } from '../lib/SignSendConfirmTransaction'
import { numberWithCommas } from '../lib/Utils'
import VaultAccount from '../on-chain/accounts/VaultAccount'
import VaultSystemState from '../on-chain/accounts/VaultSystemState'
import VaultType from '../on-chain/accounts/VaultType'
import LoadVault from '../on-chain/loaders/LoadVault'
import { redeemVaultTransaction } from '../on-chain/transactions/RedeemVaultTransactions'
import { HedgeProgram } from '../on-chain/utils/HedgeConstants'
import { setLoadingScreenMessage } from '../reducers/loadingScreenReducer'
import { RootState } from './_app'

export default function Redeem() {
  const [vaultAccounts, setVaultAccounts] = useState([])
  const [redeemingVault, setRedeemingVault] = useState()
  const [modalShow, setModalShow] = useState(false)

  const [transactionSolTransferred, setTransactionSolTransferred] = useState(0)
  const [transactionFeeLamports, setTransactionFeeLamports] = useState(0)

  const [redeemInput, setRedeemInput] = useState('')

  const { connection } = useConnection()
  const { publicKey: userWalletPublicKey, wallet } = useWallet()
  const Wallet = useWallet()

  const vaultTypes: VaultType[] = useSelector((state: RootState) => state.wallet.vaultTypes)

  return (
    <MainLayout>
      <OverviewBar />
      <Container>
        <Row className="mb-1">
          <Col>
            <h2>Redeem Vaults</h2>
            <p className="mt-3">
              You may choose to redeem your USH against its underlying SOL value at any time. This
              is done by buying back the debt of a vault of your choice. Note that you will be
              charged a fee based on the amount redeemed as well as the collateral ratio of the
              vault - the higher the amount redeemed as well as the collateral ratio, the higher the
              fee.
            </p>
            <p className="mt-3">
              This feature is offered in case USH is trading under $1 and provides a floor price
              guarantee.
            </p>
            <p className="mt-3 pt-5 text-center h4">
              Use this feature with caution, you will usually be better off exchanging your USH on
              an exchange!
            </p>
            <p className="mt-3"></p>
            {vaultTypes.map((vaultType) => {
              return <RedeemVaultType vaultType={vaultType} key={vaultType.publicKey.toString()} />
            })}
          </Col>
        </Row>
      </Container>
    </MainLayout>
  )
}

function RedeemVaultType({ vaultType }: { vaultType: VaultType }) {
  const [firstVaultToRedeem, setFirstVaultToRedeem] = useState<VaultAccount>()
  const [loadingVault, setLoadingVault] = useState(true)
  const [processingTransaction, setProcessingTransaction] = useState(false)
  const { connection } = useConnection()
  const Wallet = useWallet()
  const { publicKey: userWalletPublicKey } = useWallet()
  const recentPrices = useSelector((state: RootState) => state.wallet.recentPrices)
  const ushPrice = useSelector((state: RootState) => state.wallet.ushPrice)
  const vaultSystemState = useSelector(
    (state: RootState) => state.wallet.vaultSystemState
  ) as VaultSystemState
  const dispatch = useDispatch()
  const mountedRef = useRef(false)
  const [jupiterExchangeAmount, setJupiterExchangeAmount] = useState(0)

  // effect just for tracking mounted state
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const reloadVaults = () => {
    setLoadingVault(true)
    const program = HedgeProgram(connection, Wallet)
    if (!vaultType.firstVaultToRedeem) {
      return
    }
    LoadVault(program, vaultType.firstVaultToRedeem).then((vault) => {
      console.log('Loaded vault', vault)
      setLoadingVault(false)
      setFirstVaultToRedeem(vault)
    })
  }

  useEffect(() => {
    reloadVaults()
  }, [connection, Wallet])

  useEffect(() => {
    if (firstVaultToRedeem && vaultType.redeemable) {
      fetch(
        `https://quote-api.jup.ag/v1/quote?inputMint=9iLH8T7zoWhY7sBmj1WK9ENbWdS1nL8n9wAxaeRitTa6&outputMint=${
          vaultType.collateralMint
        }&amount=${firstVaultToRedeem.getTotalDebt().mul(LAMPORTS_PER_SOL)}&slippage=10`
      )
        .then((response) => response.json())
        .then((data) => {
          setJupiterExchangeAmount(data?.data[0]?.outAmount)
        })
    }
  }, [firstVaultToRedeem])

  const jupiterSwapUrl = ``

  const collateralPrice = recentPrices.get(vaultType.collateral)

  if (!firstVaultToRedeem || !collateralPrice || !vaultSystemState || !ushPrice) {
    return (
      <>
        <Row className="my-5 py-5 align-items-center">
          <Col lg={{ span: 4, offset: 3 }}>
            <Card>
              <Card.Header className="text-center">
                Redeem Vault Type: {vaultType.displayName()}
              </Card.Header>
              <Card.Body className='text-center p-5 my-5'>Loading...</Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    )
  }

  const redeemAmount = firstVaultToRedeem.getTotalDebt().mul(LAMPORTS_PER_SOL)

  const redeemAmountFee = new Decimal(0.5).mul(redeemAmount).div(vaultType.getTotalDebtExtended())

  const vaultUnderWater = firstVaultToRedeem.getCollateralRatio(collateralPrice).lessThan(1)

  const vaultRatioFee = firstVaultToRedeem.getCollateralRatio(collateralPrice).lessThan(3)
    ? new Decimal(0.005).mul(firstVaultToRedeem.getCollateralRatio(collateralPrice).sub(1)).div(2)
    : new Decimal(0.005)

  const timeDiffDays = new Decimal(Date.now() / 1000)
    .sub(vaultType.lastRedeemTimestamp)
    .div(60 * 60 * 24)

  const feeTimeDecay = vaultType.lastRedeemFeeBytes.mul(new Decimal(0.9).pow(timeDiffDays))

  const baseFee = new Decimal(0.005)

  const feeInPercent = redeemAmountFee.add(vaultRatioFee).add(baseFee).add(feeTimeDecay)

  const feeInUsh = feeInPercent.mul(redeemAmount).div(LAMPORTS_PER_SOL)

  const ushRedeemedAfterFee = redeemAmount.mul(new Decimal(1).sub(feeInPercent))

  const ushRedeemedAfterFeeWhole = ushRedeemedAfterFee.div(LAMPORTS_PER_SOL)

  const redeemGetCollateral = ushRedeemedAfterFeeWhole
    .div(collateralPrice)
    .mul(vaultType.lamportsPerCollateralUnit)

  const marketGetCollateral = firstVaultToRedeem.getTotalDebt().mul(ushPrice).div(collateralPrice)

  const onClickRedeem = async () => {
    console.log('ushRedeemedAfterFeeWhole', ushRedeemedAfterFeeWhole.toString())
    console.log('ushRedeemedAfterFee', ushRedeemedAfterFee.toString())
    console.log('Collateral Price Pyth', collateralPrice)
    console.log('total USH', vaultSystemState.totalUshSupply.toString())
    console.log('Amount Fee', redeemAmountFee.toString())
    console.log('Base Fee', baseFee.toString())
    console.log('Ratio Fee', vaultRatioFee.toString())
    console.log('Time Decay Fee', feeTimeDecay.toString())
    console.log('Total Fee', feeInPercent.toString())

    const program = HedgeProgram(connection, Wallet)
    dispatch(setLoadingScreenMessage('Building Transaction'))

    const transactionStep = await redeemVaultTransaction(
      program,
      userWalletPublicKey,
      firstVaultToRedeem.publicKey,
      vaultType,
      vaultType.collateralMint,
      new BN(redeemAmount.floor().toString())
    )

    setProcessingTransaction(true)
    SignSendConfirmTransaction(connection, Wallet, [transactionStep], dispatch, 'Redeem Vault')
      .then((resultSignature) => {
        console.log('Signature Result', resultSignature)
        if (mountedRef.current) {
          reloadVaults()
          setProcessingTransaction(false)
        }
      })
      .catch((error) => {
        if (mountedRef.current) {
          reloadVaults()
          setProcessingTransaction(false)
        }
      })
  }

  const renderButton = () => {
    if (!userWalletPublicKey) {
      return (
        <button className="btn btn-primary" disabled={true}>
          No wallet connected...
        </button>
      )
    }
    if (!vaultType.redeemable) {
      return (
        <button className="btn btn-primary" disabled={true}>
          Vault type not redeemable
        </button>
      )
    }
    if (vaultUnderWater) {
      return (
        <button className="btn btn-primary" disabled={true}>
          Vault below 100% Collateral Ratio
        </button>
      )
    }
    return (
      <button className="btn btn-primary" onClick={onClickRedeem}>
        Redeem Vault
      </button>
    )
  }

  return (
    <>
      <Row className="my-5 py-5 align-items-center">
        <Col lg={{ span: 4, offset: 3 }}>
          <Card>
            <Card.Header className="text-center p-3">
              <Row className="align-items-center">
                <Col className="text-center">
                  <div className="position-relative">
                    <img
                      alt="Coin Image"
                      src={`/crypto-logos/${vaultType.collateralIcon}`}
                      width={90}
                      height={90}
                      className="rounded-circle translate-middle"
                      style={{
                        // transform: 'translateY(-68px)',
                        position: 'absolute',
                        top: -45,
                        // left: 45
                      }}
                    />
                  </div>
                </Col>
                <Col className="text-center">
                  <div className="fw-bold text-primary">Collateral</div>
                  <div>{vaultType.collateral}</div>
                </Col>
                <Col className="text-center">
                  <div className="fw-bold text-primary">Min Ratio</div>
                  <div>{vaultType.displayLiquidationCollateralRatio()}%</div>
                </Col>
                <Col className="text-center">
                  <div className="fw-bold text-primary">Interest</div>
                  <div>{vaultType.displayInterestRate()}%</div>
                </Col>
              </Row>
            </Card.Header>
            {/* <hr className="my-1" /> */}
            <Card.Body className="p-4">
              <Row className="fw-bold text-light">
                <Col>Vault Overview</Col>
              </Row>
              <Row className="mt-1">
                <Col>Vault Debt</Col>
                <Col className="text-end">
                  <span className="font-monospace">
                    {numberWithCommas(firstVaultToRedeem.getTotalDebt(), 4)}
                  </span>{' '}
                  USH
                </Col>
              </Row>
              <Row className="">
                <Col>Vault Collateral</Col>
                <Col className="text-end">
                  <span className="font-monospace">
                    {numberWithCommas(firstVaultToRedeem.getCollateral(), 4)}
                  </span>{' '}
                  {vaultType.collateral}
                </Col>
              </Row>
              <Row className="mb-4">
                <Col>Collateral Ratio</Col>
                <Col className="text-end">
                  <span className="font-monospace">
                    {numberWithCommas(
                      firstVaultToRedeem.getCollateralRatio(collateralPrice).mul(100),
                      1
                    )}
                    %
                  </span>
                </Col>
              </Row>
              <Row className="fw-bold text-light">
                <Col>Redeem Transaction Preview</Col>
              </Row>
              <Row className="mt-1">
                <Col>Pay</Col>
                <Col className="text-end">
                  <span className="font-monospace">
                    {redeemAmount.div(LAMPORTS_PER_SOL).toFixed(4)}
                  </span>{' '}
                  USH
                </Col>
              </Row>
              <Row className="">
                <Col>Fee</Col>
                <Col className="text-end">
                  <span className="font-monospace">{feeInUsh.toFixed(4)}</span> USH
                </Col>
              </Row>
              <Row className="mb-4">
                <Col>Receive</Col>
                <Col className="text-end">
                  <span className="font-monospace">
                    {numberWithCommas(
                      redeemGetCollateral.div(vaultType.lamportsPerCollateralUnit),
                      4
                    )}
                  </span>{' '}
                  {vaultType.collateral}
                </Col>
              </Row>
              <Row className="">
                <Col>USH price for redeem</Col>
                <Col className="text-end">
                  <span className="font-monospace">
                    {numberWithCommas(
                      redeemGetCollateral
                        .div(vaultType.lamportsPerCollateralUnit)
                        .mul(collateralPrice)
                        .div(redeemAmount)
                        .mul(LAMPORTS_PER_SOL),
                      4
                    )}
                  </span>{' '}
                  USD
                </Col>
              </Row>
              <Row className="">
                <Col>Approx. loss for redeemer</Col>
                <Col className="text-end">
                  <span className="font-monospace">
                    {numberWithCommas(
                      marketGetCollateral.minus(ushRedeemedAfterFeeWhole.div(collateralPrice)),
                      4
                    )}{' '}
                    {vaultType.collateral}
                  </span>{' '}
                </Col>
              </Row>
              <Row className="mb-4">
                <Col></Col>
                <Col className="text-end">
                  <span className="font-monospace">
                    ($
                    {numberWithCommas(
                      marketGetCollateral
                        .minus(ushRedeemedAfterFeeWhole.div(collateralPrice))
                        .mul(collateralPrice),
                      2
                    )}{' '}
                    USD)
                  </span>{' '}
                </Col>
              </Row>
              <div className="d-grid gap-2 mb-1">{renderButton()}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={{ span: 3 }} className="text-start">
          <Row>
            <Col className="my-3">
              <div>
                Vaults are redeemed in order from most risky to least risky (lowest collateral ratio
                to highest collateral ratio).
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="mt-3">
              <div>
                At this time we only support full redemptions of individual vaults, in order. If you
                wish to redeem more than the vaults total debt, please redeem multiple vaults.
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="my-3">
              <hr />
              <div>
                {vaultType.redeemable
                  ? 'Total redeemable amount for this vault type:'
                  : 'This type of vault is not able to be redeemed against. The total outstanding debt is:'}
              </div>
              <div className="h2">
                {numberWithCommas(vaultType.getTotalDebtExtended().div(LAMPORTS_PER_SOL), 2)} USH
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
}
